<script lang="ts" setup>
import { storeToRefs } from "pinia";
import ChatIcon from "@/components/icon/chatIcon.vue";
import HappyFaceIcon from "@/components/icon/HappyFaceIcon.vue";
import MagicStickIcon from "@/components/icon/MagicStickIcon.vue";
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import FollowIcon from "@/components/icon/FollowIcon.vue";
import AdmireIcon from "@/components/icon/AdmireIcon.vue";
import RequestIcon from "@/components/icon/RequestIcon.vue";
import BookIcon from "@/components/icon/BookIcon.vue";
import { usePushChannel } from "@/stores/push-channel";
import MentionIcon from "@/components/icon/MentionIcon.vue";
import VideoIcon from "@/components/icon/VideoIcon.vue";

const pusher = usePushChannel();
const { popup } = storeToRefs(pusher);
const startY = ref(0);
const endY = ref<number | null>(null);
const transformY = ref(0);
watch(
  () => pusher.popup.show,
  (show) => {
    if (show) {
      startY.value = 0;
      endY.value = null;
      transformY.value = 0;
      setTimeout(() => {
        pusher.popup.show = false;
      }, 3000);
    } else {
      pusher.resetPopup();
    }
  }
);
const handleTouchStart = (event: TouchEvent) => {
  startY.value = event.touches[0].clientY;
};
const handleTouchMove = (event: TouchEvent) => {
  endY.value = event.touches[0].clientY;
  const diff = endY.value - startY.value;
  if (diff > 0) {
    // 向下滑动，不处理
    return;
  }
  transformY.value = diff;
};
const handleTouchEnd = () => {
  if (endY.value && endY.value - startY.value < -50) {
    popup.value.show = false;
  } else {
    transformY.value = 0;
  }
};
const router = useRouter();
const onClick = (event: UIEvent) => {
  event.stopPropagation();
  popup.value.onClick(router);
  popup.value.show = false;
};
</script>
<template>
  <Teleport to="#app">
    <transition name="slide">
      <div
        v-show="popup.show"
        :style="{ transform: `translateY(${transformY}px)` }"
        class="global-notification absolute top-5 left-5 right-5 rounded-lg shadow-2xl bg-base-100 grid grid-cols-[auto_1fr] border/20 h-20 overflow-hidden z-[100]"
        @click="onClick"
        @touchend="handleTouchEnd"
        @touchmove="handleTouchMove"
        @touchstart="handleTouchStart"
      >
        <div
          class="h-full flex items-center justify-center w-10 bg-amber-500 text-white"
        >
          <chat-icon
            v-if="
              popup.type === 4 ||
              popup.type === 5 ||
              popup.type === 26 ||
              popup.type === 27
            "
            class="icon-2xl"
          />
          <happy-face-icon
            v-else-if="popup.type === 6 || popup.type === 28"
            class="icon-2xl"
          />
          <magic-stick-icon v-else-if="popup.type === 7" class="icon-2xl" />
          <request-icon
            v-else-if="popup.type === 8 || popup.type === 12"
            class="icon-2xl"
          />
          <chat-icon v-else-if="popup.type === 9" class="icon-2xl" />
          <follow-icon v-else-if="popup.type === 10" class="icon-2xl" />
          <admire-icon v-else-if="popup.type === 11" class="icon-2xl" />
          <book-icon v-else-if="popup.type === 13" class="icon-2xl" />
          <video-icon v-else-if="popup.type === 15" class="icon-2xl" />
          <mention-icon
            v-else-if="
              popup.type === 14 || popup.type === 16 || popup.type === 29
            "
            class="icon-2xl"
          />
          <i
            v-else-if="popup.type === 20"
            class="iconify mdi--ticket text-lg"
          />
          <i v-else class="iconify mingcute--information-line text-xl" />
        </div>
        <div class="p-4 flex gap-2 w-full overflow-hidden items-center">
          <img
            v-show="popup.avatarUrl"
            :src="popup.avatarUrl"
            alt=""
            class="h-full object-contain"
          />
          <div class="flex flex-col justify-between overflow-hidden">
            <div v-show="popup.mateName" class="text-base-content/50">
              <span class="font-bold">@{{ popup.mateName }}</span>
              {{ $t("Notifications.receiveNewNotification") }}
            </div>
            <div class="font-bold">{{ popup.title }}</div>
            <div
              v-show="popup.content"
              class="whitespace-nowrap overflow-ellipsis overflow-hidden"
            >
              {{ popup.content }}
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>
<style>
.global-notification {
  user-select: none;
}

.slide-leave-active,
.slide-enter-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(100%);
}
</style>
